<script setup lang="ts">
import { useForm } from '@inertiajs/vue3';
import { PlusCircle } from 'lucide-vue-next';
import { ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const props = defineProps<{
    projectId: string;
    storeUrl: string;
}>();

const open = ref(false);

const form = useForm({
    title: '',
    authors: '',
    year: '',
    doi: '',
    abstract: '',
});

function submit() {
    form.transform((data) => ({
        ...data,
        year: data.year ? parseInt(data.year) : null,
        authors: data.authors
            ? data.authors
                  .split(/[,;]/)
                  .map((a) => a.trim())
                  .filter(Boolean)
            : [],
    })).post(props.storeUrl, {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
            open.value = false;
        },
    });
}
</script>

<template>
    <Dialog v-model:open="open">
        <DialogTrigger as-child>
            <Button size="sm" variant="outline" class="h-8 gap-1.5 text-xs">
                <PlusCircle class="h-3.5 w-3.5" />
                Ajouter
            </Button>
        </DialogTrigger>

        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>Ajouter une référence manuelle</DialogTitle>
            </DialogHeader>

            <form class="space-y-4" @submit.prevent="submit">
                <div class="space-y-1.5">
                    <Label for="ref-title"
                        >Titre <span class="text-destructive">*</span></Label
                    >
                    <Input
                        id="ref-title"
                        v-model="form.title"
                        placeholder="Titre de l'article ou de l'ouvrage"
                        :class="form.errors.title ? 'border-destructive' : ''"
                        required
                    />
                    <p
                        v-if="form.errors.title"
                        class="text-xs text-destructive"
                    >
                        {{ form.errors.title }}
                    </p>
                </div>

                <div class="space-y-1.5">
                    <Label for="ref-authors">Auteurs</Label>
                    <Input
                        id="ref-authors"
                        v-model="form.authors"
                        placeholder="Ex: Dupont Jean, Martin Sophie"
                    />
                    <p class="text-[11px] text-muted-foreground">
                        Séparés par des virgules ou des points-virgules
                    </p>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="space-y-1.5">
                        <Label for="ref-year">Année</Label>
                        <Input
                            id="ref-year"
                            v-model="form.year"
                            type="number"
                            placeholder="2024"
                            min="1000"
                            max="9999"
                        />
                    </div>
                    <div class="space-y-1.5">
                        <Label for="ref-doi">DOI</Label>
                        <Input
                            id="ref-doi"
                            v-model="form.doi"
                            placeholder="10.1234/..."
                        />
                    </div>
                </div>

                <div class="space-y-1.5">
                    <Label for="ref-abstract">Résumé</Label>
                    <Textarea
                        id="ref-abstract"
                        v-model="form.abstract"
                        placeholder="Résumé (optionnel)"
                        class="resize-none"
                        rows="3"
                    />
                </div>

                <div class="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        @click="open = false"
                        >Annuler</Button
                    >
                    <Button type="submit" :disabled="form.processing">
                        {{
                            form.processing ? 'Ajout…' : 'Ajouter la référence'
                        }}
                    </Button>
                </div>
            </form>
        </DialogContent>
    </Dialog>
</template>
